import * as React from 'react';

import { ModalContext } from '@notino/react-styleguide';

import { IAddToCartButtonProps } from '@sharedComponents/AddToCartButton/model';

import { runTrackingEventCartUpdate } from '../utils';

import {
  IActions,
  Action,
  ActionTypes,
  IHandleProductAddedToCartParams,
} from './model';

export const useActions = (
  dispatch: React.Dispatch<Action>,
  options: Pick<IAddToCartButtonProps, 'getModalAfterAddToCart'>
): IActions => {
  const { toggleModal } = ModalContext.useModalContext();
  const { getModalAfterAddToCart } = options;

  const handleCloseModal = React.useCallback(() => {
    dispatch({ type: ActionTypes.HANDLE_CLOSE_MODAL });
  }, [dispatch]);

  const setMouseOverBuyButton = React.useCallback(() => {
    dispatch({ type: ActionTypes.SET_MOUSE_OVER_BUY_BUTTON });
  }, [dispatch]);

  const handleIncreaseSubmitAttempts = React.useCallback(() => {
    dispatch({
      type: ActionTypes.INCREASE_SUBMIT_ATTEMPTS,
    });
  }, [dispatch]);

  const handleProductAddedToCart = React.useCallback(
    ({
      handleGtmPush,
      result,
      products,
      modal,
    }: IHandleProductAddedToCartParams) => {
      if (result && (result.success || result.notificationMessage)) {
        handleGtmPush();
        products.forEach(({ id, count }) =>
          runTrackingEventCartUpdate({
            productId: id,
            quantity: count,
            action: 'add',
          })
        );

        dispatch({ type: ActionTypes.HANDLE_SUCCESS_ADD_TO_CART });
        if (modal) {
          const modalProps = getModalAfterAddToCart({
            products,
            ...modal,
            ...result,
          });
          modalProps && toggleModal(...modalProps);
        }
      } else {
        dispatch({
          type: ActionTypes.HANDLE_FAILED_ADD_TO_CART,
        });
      }
    },
    [dispatch, toggleModal, getModalAfterAddToCart]
  );

  const handleFailedAddToCart = React.useCallback(() => {
    dispatch({ type: ActionTypes.HANDLE_FAILED_ADD_TO_CART });
  }, [dispatch]);

  const showSpecialModal = React.useCallback(
    (modal, header) => {
      toggleModal(modal, {
        header,
        center: true,
        positionBottomOnMobile: true,
      });
    },
    [toggleModal]
  );

  const updateStateAfterPropsChange = React.useCallback(
    (newState) => {
      dispatch({
        type: ActionTypes.UPDATE_STATE_AFTER_PROP_CHANGE,
        payload: newState,
      });
    },
    [dispatch]
  );

  return {
    handleCloseModal,
    setMouseOverBuyButton,
    handleProductAddedToCart,
    showSpecialModal,
    handleFailedAddToCart,
    handleIncreaseSubmitAttempts,
    updateStateAfterPropsChange,
  };
};
